import React from 'react';
import { useState } from 'react';
import _ from 'underscore';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { SettingsCoordinator } from '~settings-coordinator/SettingsCoordinator';
import { ErrorReason } from '~src/sessionManagement/SessionManagementService';
import {
  DataConnectSiteSettings,
  DataConnectSiteSettingsProps,
  SiteDataConnectSettingsForSiteAdminProps
} from '~src/settings/dataconnect-settings/DataConnectSiteSettings';
import { BaasEdgePoolAndCluster } from '~src/settings/dataconnect-settings/serverApi/resources/BaasEdgePoolAndCluster';

import { DataConnectSiteSettingsDefault } from './DataConnectSiteSettingsDefault';
import { BaasEdgePoolsAndClustersPayload } from './sections/types/BaasEdgePoolsAndClustersPayload';
import { ClusterStatusPollService } from './sections/utils/ClusterStatusPollService';
import { getGeneralErrorMessage } from './sections/utils/EdgePoolErrorUtilities';
import { IGetBaasClusterDetailsResult } from './serverApi/requests/GetBaasClusterDetailsRequest';
import { IGetBaasEdgePoolsAndClustersResult } from './serverApi/requests/GetBaasEdgePoolsAndClustersRequest';

const initialEdgePoolsAndClusters: BaasEdgePoolsAndClustersPayload = {
  userDefinedPools: {},
  userClusters: {},
  bridgeDomains: {},
  blockedDomains: {},
  maxNodes: 0,
  nodesCount: 0,
  allowCreateCluster: false,
  countOfSharedCluster: 0,
  siteCountOfTenant: 0,
  siteId: ''
};

/**
 * Creates a settings coordinator with the site DataConnect settings
 */
export function DataConnectSiteSettingsCoordinator(props: SiteDataConnectSettingsForSiteAdminProps) {
  const [baasEdgePoolsAndClusters, setPoolsAndClusters] = useState(initialEdgePoolsAndClusters);
  const [hasLoadedPoolsAndClusters, setHasLoadedPoolsAndClusters] = useState(false);


  const refreshPoolsAndClusters = React.useCallback(() => {
    BaasEdgePoolAndCluster.getBaasPoolsAndCluster({ siteId: props.settings.siteId})
      .then((result: IGetBaasEdgePoolsAndClustersResult) => {
        if (result.success) {
          setPoolsAndClusters(result.success);
        }
        if (result.errors) {
          ClusterStatusPollService.cancelClusterDetailsStatsPolling();
          ToasterService.instance.pop({
            type: ToastType.Error,
            text: getGeneralErrorMessage(result.errors[0].code, result.errors[0].message)
          });
        }
      });
  }, [props.settings.siteId]);

  React.useEffect(() => {
    FeatureFlags.edgeMultiPoolEnabled && BaasEdgePoolAndCluster.getBaasPoolsAndCluster({ siteId: props.settings.siteId})
      .then((result: IGetBaasEdgePoolsAndClustersResult) => {
        if (result.success) {
          setPoolsAndClusters(result.success);
          setHasLoadedPoolsAndClusters(true);
        }
        if (result.errors) {
          ToasterService.instance.pop({
            type: ToastType.Error,
            text: getGeneralErrorMessage(result.errors[0].code, result.errors[0].message)
          });
        }
      });
  }, [props.settings.siteId]);

  React.useEffect(
    () => {
      var clusters = baasEdgePoolsAndClusters.userClusters;
      if (hasLoadedPoolsAndClusters && Object.keys(clusters).length>0) {
        let shouldSetState = true;
        const callbackWithResult = (result: IGetBaasClusterDetailsResult) => {
          if (result?.errors) {
            // any error happens, stop the poll service
            ClusterStatusPollService.cancelClusterDetailsStatsPolling();
          } else {

            Object.keys(clusters).forEach(clusterId => {
              if (!_.isEqual(clusters[clusterId],result.success[clusterId])) {
                //trigger the reload
                refreshPoolsAndClusters();
              }
          });
          }
        };
        const callbackWithError = (reason: ErrorReason) => {
          // if the request was canncelled, don't stop poller
          if (ErrorReason.Canceled !== reason) {
            ClusterStatusPollService.cancelClusterDetailsStatsPolling();
          }
        };

        ClusterStatusPollService.pollClusterDetailsStats(clusters, props.settings.siteId, callbackWithResult).then(
          callbackWithResult, // success
          callbackWithError, // error
        );

        return () => {
          ClusterStatusPollService.cancelClusterDetailsStatsPolling();
          shouldSetState = false;
        };
      }
    },
    [hasLoadedPoolsAndClusters, baasEdgePoolsAndClusters, props.settings.siteId, refreshPoolsAndClusters]
  );

  const newProps: DataConnectSiteSettingsProps = {
    settings: props.settings,
    baasEdgePoolsAndClusters: baasEdgePoolsAndClusters,
    refreshPoolsAndClusters,
    hasLoadedPoolsAndClusters
  };

  return (<>
      {props.settings.siteAdminDataConnectSettings?.maxNodes === 0?
       <DataConnectSiteSettingsDefault {...newProps} />
       :<SettingsCoordinator>
        <DataConnectSiteSettings {...newProps} />
       </SettingsCoordinator>}
      </>
  );
}